import { clsx } from 'clsx';
import type { RatingState } from '#shared/core/ratings';
import { formatDelta, ratingText, tierName, tierOf } from '@/lib/rating';
import { uiCopy } from './copy';
import { Tag } from './Tag';
import { TierFrame } from './TierFrame';

/**
 * OVR in its tier shape, with the tier name as text so color is never the only cue.
 * Unrated shows ?? on the dashed frame with "Combine pending". Single digits are fine: 0 is
 * "0" and 7 is "7", centered in a fixed two-character slot so 7 and 77 take the same width.
 *
 *   <RatingBadge value={76} delta={2} />
 *   <RatingBadge value={null} />                         ?? / Combine pending
 *   <RatingBadge value={61} state="provisional" size="lg" />
 *   <RatingBadge value={88} size="sm" showTier={false} /> tier still in the accessible name
 */
export type RatingBadgeSize = 'sm' | 'md' | 'lg';

const SHAPE_PX: Record<RatingBadgeSize, number> = { sm: 40, md: 56, lg: 88 };
const NUM_CLS: Record<RatingBadgeSize, string> = { sm: 'text-lg', md: 'text-2xl', lg: 'text-4xl' };

export interface RatingBadgeProps {
  value: number | null | undefined;
  delta?: number | null;
  state?: RatingState;
  size?: RatingBadgeSize;
  /** Print the tier name next to the shape. Default true. It is in the accessible name either way. */
  showTier?: boolean;
  /** What the number is. Default "OVR". */
  metric?: string;
  className?: string;
}

export function ratingBadgeLabel(
  value: number | null | undefined,
  { state, delta, metric = 'OVR' }: { state?: RatingState; delta?: number | null; metric?: string } = {},
): string {
  const tier = tierOf(value);
  const parts = [
    tier ? `${metric} ${ratingText(value)}` : `${metric} unknown`,
    tier ? `${tierName(tier)} tier` : uiCopy.rating.pending,
  ];
  if (state === 'provisional') parts.push('provisional');
  if (state === 'rusty') parts.push('rusty');
  if (delta) parts.push(delta > 0 ? `up ${delta}` : `down ${Math.abs(delta)}`);
  return parts.join(', ');
}

export function RatingBadge({
  value,
  delta,
  state,
  size = 'md',
  showTier = true,
  metric = 'OVR',
  className,
}: RatingBadgeProps) {
  const tier = tierOf(value);
  const text = ratingText(value);
  const px = SHAPE_PX[size];
  return (
    <span
      role="img"
      aria-label={ratingBadgeLabel(value, { state, delta, metric })}
      className={clsx('inline-flex items-center gap-2', className)}
    >
      <TierFrame tier={tier} size={px}>
        <span
          className={clsx(
            'tts-display inline-block w-[2ch] text-center tabular-nums leading-none',
            NUM_CLS[size],
            tier === null && 'text-ink-soft',
          )}
        >
          {text}
        </span>
      </TierFrame>
      {(showTier || state === 'provisional' || state === 'rusty' || delta) && (
        <span className="flex flex-col items-start gap-1" aria-hidden="true">
          {showTier && (
            <span className="font-display text-xs font-black uppercase italic leading-none tracking-wide">
              {tier ? tierName(tier) : uiCopy.rating.pending}
            </span>
          )}
          {state === 'provisional' && <Tag tone="butter">{uiCopy.rating.provisional}</Tag>}
          {state === 'rusty' && <Tag tone="peach">{uiCopy.rating.rusty}</Tag>}
          {delta ? <DeltaPill delta={delta} /> : null}
        </span>
      )}
    </span>
  );
}

/**
 * "+2" on mint with an up arrow, "−1" on peach with a down arrow. The arrow and the sign carry
 * the meaning, the fill only repeats it.
 */
export function DeltaPill({ delta, className }: { delta: number; className?: string }) {
  const up = delta > 0;
  const flat = delta === 0;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 rounded-chip border-2 border-ink px-1.5 font-display text-sm font-black italic leading-5 tabular-nums text-ink',
        flat ? 'bg-white' : up ? 'bg-mint' : 'bg-peach',
        className,
      )}
    >
      <span aria-hidden="true">{flat ? '=' : up ? '▲' : '▼'}</span>
      {formatDelta(delta)}
    </span>
  );
}
